import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';

export type RecentKind = 'cliente' | 'ticket' | 'macchina';

export interface RecentItem {
  kind: RecentKind;
  id: string;
  label: string;
  href: string;
  visitedAt: number;
}

const MAX_ITEMS = 8;

function storageKey(userId: string): string {
  return `wenkey.recent.${userId}`;
}

function readItems(userId: string): RecentItem[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecentItem).slice(0, MAX_ITEMS);
  } catch {
    return [];
  }
}

function isRecentItem(value: unknown): value is RecentItem {
  if (!value || typeof value !== 'object') return false;
  const item = value as Partial<RecentItem>;
  return (
    (item.kind === 'cliente' || item.kind === 'ticket' || item.kind === 'macchina') &&
    typeof item.id === 'string' &&
    typeof item.label === 'string' &&
    typeof item.href === 'string' &&
    typeof item.visitedAt === 'number'
  );
}

/**
 * Last 8 visited clients, tickets and machines, keyed by user in localStorage.
 */
export function useRecentItems(): {
  items: RecentItem[];
  record: (item: Omit<RecentItem, 'visitedAt'>) => void;
} {
  const userId = useAuthStore((state) => state.user?.id);
  const [items, setItems] = useState<RecentItem[]>([]);

  useEffect(() => {
    if (!userId) {
      setItems([]);
      return;
    }
    setItems(readItems(userId));
  }, [userId]);

  const record = useCallback(
    (item: Omit<RecentItem, 'visitedAt'>) => {
      if (!userId) return;
      setItems((previous) => {
        const next: RecentItem[] = [
          { ...item, visitedAt: Date.now() },
          ...previous.filter((entry) => !(entry.kind === item.kind && entry.id === item.id)),
        ].slice(0, MAX_ITEMS);
        localStorage.setItem(storageKey(userId), JSON.stringify(next));
        return next;
      });
    },
    [userId],
  );

  return { items, record };
}
